import type { SponsoredBrandAsset, SponsoredBrandStore } from "@adpilot/shared";
import type { AdFormat } from "./sponsored-brands-model";

/**
 * Stores usable for the chosen brand (matched by brandId, which vendors have and brandEntityId
 * they lack); Store Spotlight needs at least four pages with products.
 */
export function filterEligibleStores(
  stores: SponsoredBrandStore[],
  brandId: string | null,
  adFormat: AdFormat,
): SponsoredBrandStore[] {
  return stores.filter(
    (store) =>
      (!brandId || store.brandId === brandId) &&
      (adFormat !== "storeSpotlight" ||
        store.pages.filter((page) => page.asins.length > 0).length >= 4),
  );
}

/** Splits the brand asset library into the assets each creative slot accepts. */
export function selectCreativeAssets(assets: SponsoredBrandAsset[]): {
  logoAssets: SponsoredBrandAsset[];
  customImageAssets: SponsoredBrandAsset[];
  videoAssets: SponsoredBrandAsset[];
} {
  const imageAssets = assets.filter(
    (asset) =>
      asset.type === "IMAGE" &&
      asset.status === "ACTIVE" &&
      !Object.values(asset.moderationPolicies).includes("REJECTED") &&
      ["image/png", "image/jpeg"].includes(asset.contentType ?? ""),
  );
  const logoAssets = imageAssets.filter(
    (asset) =>
      asset.subtypes.some((subtype) => subtype.toUpperCase() === "LOGO") &&
      asset.width >= 400 &&
      asset.height >= 400 &&
      asset.sizeBytes <= 1_000_000,
  );
  const customImageAssets = imageAssets.filter(
    (asset) => asset.width >= 1_200 && asset.height >= 628 && asset.sizeBytes <= 5_000_000,
  );
  const videoAssets = assets.filter(
    (asset) =>
      asset.type === "VIDEO" &&
      asset.status === "ACTIVE" &&
      asset.durationSeconds >= 6 &&
      asset.durationSeconds <= 45 &&
      asset.sizeBytes <= 500_000_000 &&
      ["video/mp4", "video/quicktime"].includes(asset.contentType ?? "") &&
      (asset.approvedPrograms.length === 0 ||
        asset.approvedPrograms.includes("SPONSORED_BRANDS_VIDEO")),
  );
  return { logoAssets, customImageAssets, videoAssets };
}
